import { useQuery } from '@tanstack/react-query';
import { getRolloutToken } from '../../api/rollout';

interface UseRolloutTokenOptions {
  /** Organization ID to fetch token for */
  orgId: string | undefined;
  /** Whether the query should be enabled (e.g., when modal is open) */
  enabled?: boolean;
}

/**
 * React Query hook for fetching Rollout token
 * Eliminates the need for useEffect-based data fetching
 *
 * @example
 * const { data: token, isLoading, error } = useRolloutToken({
 *   orgId: selectedOrg?.id,
 *   enabled: isOpen,
 * });
 */
export const useRolloutToken = ({ orgId, enabled = true }: UseRolloutTokenOptions) => {
  return useQuery({
    queryKey: ['rollout-token', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      const response = await getRolloutToken(orgId);
      return response.token;
    },
    enabled: enabled && !!orgId,
    // Token is short-lived, don't cache for too long
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 1,
  });
};
